import { inject, Injectable, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AsinSeed,
  AsinSeedTypeEnum,
  CostOfGoods,
  CustomField,
  Marketplace,
  MatchType,
  Strategy,
  StrategyStateEnum,
} from "@front/m19-api-client";
import { Catalog, Marketplaces, ProductEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  AuthService,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
  UserSelectionService,
} from "@front/m19-services";
import { ModalService } from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { ProductTagField } from "@m19-board/catalog/catalog-sections/catalog.component";
import { SeedCsvRow } from "@m19-board/catalog/catalog-sections/seed-csv-row";
import {
  CogsUpdateModalComponent,
  CogsUpdateModalData,
} from "@m19-board/catalog/cog-update-modal/cogs-upload-modal.component";
import {
  CustomFieldEditionComponent,
  CustomFieldEditionModalData,
  CustomFieldEditionOutput,
} from "@m19-board/catalog/custom-field-edition.component";
import { CustomFieldSettingsComponent } from "@m19-board/catalog/custom-field-settings.component";
import {
  UploadCatalogDataModalComponent,
  UploadResult,
} from "@m19-board/catalog/upload-catalog-data-modal/upload-catalog-data-modal.component";
import { FilterCategory } from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { combineLatest, filter, map, Observable, of, switchMap, take, tap } from "rxjs";
import { CsvExportParams, CsvExportService, CsvField, fieldExtractor, simpleField } from "./csv-export.service";
import { LoadingService } from "./loading.service";

export interface CatalogProductEx extends ProductEx {
  seeds?: AsinSeed[];
  blacklist?: AsinSeed[];
  keywordSeeds?: AsinSeed[];
  productSeeds?: AsinSeed[];
  keywordBlacklist?: AsinSeed[];
  productBlacklist?: AsinSeed[];
  strategies?: Strategy[];
}

@Injectable({
  providedIn: "root",
})
export class ManagedCatalogPageService {
  private readonly asinService = inject(AsinService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly loadingService = inject(LoadingService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly asinFilterService = inject(AsinFilterService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly sdStrategyService = inject(SdStrategyService);

  private readonly accountMarketplace$ = this.accountSelectionService.singleAccountMarketplaceSelection$;
  private readonly accountMarketplace = toSignal(this.accountMarketplace$, { requireSync: true });
  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });

  public readonly filters$ = this.asinFilterService.filters$.pipe(
    filter((filter) => filter.length > 0),
    map((filters) =>
      filters
        .filter(
          (filter) =>
            filter.type === FilterCategory.BrandProducts ||
            filter.type === FilterCategory.ProductTag1 ||
            filter.type === FilterCategory.ProductTag2,
        )
        .map((filter) => ({
          ...filter,
          selectAllOption: true,
        })),
    ),
    take(1),
  );

  public readonly inventoryConfig = toSignal(
    this.accountMarketplace$.pipe(
      switchMap((accountMarketplace) =>
        this.asinService.getInventoryConfig(accountMarketplace.accountId, accountMarketplace.marketplace),
      ),
    ),
  );

  private readonly _managedCatalog = toSignal(
    this.accountMarketplace$.pipe(
      tap(() => this.loadingService.loadingOn()),
      switchMap((accountMarketplace) =>
        this.getEnhancedManagedCatalog(accountMarketplace.accountId, accountMarketplace.marketplace),
      ),
      tap(() => this.loadingService.loadingOff()),
    ),
  );

  public managedCatalog = linkedSignal({
    source: this._managedCatalog,
    computation: (catalog) => catalog,
    equal: () => false,
  });

  private getEnhancedManagedCatalog(accountId: string, marketplace: Marketplace): Observable<CatalogProductEx[]> {
    return this.asinService.getCatalog(accountId, marketplace).pipe(
      switchMap((catalog: Catalog) => {
        return combineLatest([
          this.asinService.getAsinSeeds(accountId, marketplace),
          of(catalog.products),
          this.spStrategyService.getStrategiesPerAsin(accountId, marketplace),
          this.sbStrategyService.getStrategiesPerAsin(accountId, marketplace),
          this.sdStrategyService.getStrategiesPerAsin(accountId, marketplace),
        ]).pipe(
          map(([seeds, products, spStrategies, sbStrategies, sdStrategies]) =>
            products.map((product) =>
              this.expandProduct(
                product,
                seeds.get(product.asin!),
                spStrategies.get(product.asin!),
                sbStrategies.get(product.asin!),
                sdStrategies.get(product.asin!),
              ),
            ),
          ),
        );
      }),
    );
  }

  selectedRows = signal<Set<string>>(new Set<string>());

  public openProductTagSettings() {
    this.modalService.openModal(CustomFieldSettingsComponent, {
      modalTitle: this.translocoService.translate("catalog-page.custom_field_settings"),
    });
  }

  public getCurrentSelectedProductTags(asins: string[], field: ProductTagField): CustomField[] {
    return this.managedCatalog()!
      .map((product) => ({
        asin: product.asin!,
        [field]: product[field === ProductTagField.FIELD1 ? "productTag1" : "productTag2"],
      }))
      .filter((tag) => asins.includes(tag.asin!));
  }

  public updateProductTags(asins: string[], field: ProductTagField, value: string) {
    const customFields: CustomField[] = asins.map((asin) => ({ asin, [field]: value }));
    return this.accountMarketplace$.pipe(
      switchMap((accountMarketplace) =>
        this.asinService.updateProductTags(accountMarketplace.accountId, accountMarketplace.marketplace, customFields),
      ),
    );
  }

  public openProductTagEditionModal(
    asins: string[],
    field: ProductTagField,
    value?: string,
  ): Observable<CustomFieldEditionOutput> {
    const ref = this.modalService.openModal<CustomFieldEditionModalData, CustomFieldEditionOutput>(
      CustomFieldEditionComponent,
      {
        size: "md",
        modalTitle: this.translocoService.translate("catalog-page.product_tag_edition"),
        data: {
          marketplace: this.accountMarketplace().marketplace,
          productTag: field,
          value: value,
          inventoryConfig: this.inventoryConfig(),
          asin: asins.length > 1 ? undefined : asins[0],
        },
      },
    );

    return combineLatest([ref.afterClosed(), ref.afterClosed()]).pipe(map(([value]) => value));
  }

  public restoreProductTagsState(tags: CustomField[], field: ProductTagField) {
    tags.forEach((tag) => {
      this.setProductTagsState([tag.asin!], field, tag[field === ProductTagField.FIELD1 ? "field1" : "field2"]!);
    });
  }

  public setProductTagsState(asins: string[], field: ProductTagField, value: string) {
    this.managedCatalog.update((catalog) => {
      const result = catalog!.map((product) => {
        if (asins.includes(product.asin!)) {
          return { ...product, [field === ProductTagField.FIELD1 ? "productTag1" : "productTag2"]: value };
        }
        return product;
      });
      return [...result];
    });
  }

  public openCogsUpdateModal(product: CatalogProductEx): Observable<number> {
    const ref = this.modalService.openModal<CogsUpdateModalData, number>(CogsUpdateModalComponent, {
      size: "md",
      data: {
        asin: product.asin!,
        marketplace: this.accountMarketplace().marketplace,
        locale: this.locale(),
        currency: Marketplaces[this.accountMarketplace().marketplace].currency,
        cogsValue: product.costOfGoods,
      },
    });

    return ref.afterClosed().pipe(map((cogValue) => (cogValue ? +cogValue! : 0)));
  }

  public setCogsState(cogs: CostOfGoods[]) {
    this.managedCatalog.update((catalog) => {
      const result = catalog!.map((product) => {
        const cog = cogs.find((cog) => cog.asin! === product.asin!);
        if (cog) {
          return { ...product, costOfGoods: cog.costOfGoods! };
        }
        return product;
      });
      return [...result];
    });
  }

  public updateProductCogs(asin: string, cogValue: number) {
    const accountId = this.accountMarketplace().accountId;
    const marketplace = this.accountMarketplace().marketplace;
    return this.asinService.updateCostOfGood(accountId, marketplace, [
      { asin, costOfGoods: cogValue, accountId, marketplace },
    ]);
  }

  public stopManagingProducts(asins: string[]) {
    return this.asinService
      .deleteAsins(this.accountMarketplace().accountId, this.accountMarketplace().marketplace, asins)
      .pipe(
        tap(() =>
          this.managedCatalog.update((catalog) => {
            return [...catalog!.filter((product) => !asins.includes(product.asin!))];
          }),
        ),
      );
  }

  public uploadCatalogData(currencySymbol: string): void {
    this.modalService.openModal(UploadCatalogDataModalComponent, {
      size: "md",
      modalTitle: this.translocoService.translate("upload-catalog-data-modal.upload_catalog_data"),
      data: {
        currencySymbol: currencySymbol,
        accountId: this.accountMarketplace().accountId,
        marketplace: this.accountMarketplace().marketplace,
        catalogAsins: this.managedCatalog(),
        onCogsUpload: (cogs: UploadResult<CostOfGoods>) => {
          this.setCogsState(cogs.uploaded);
        },
        onSeedsUpload: () => {},
        onCFsUpload: (productTags: UploadResult<CustomField>) => {
          productTags.uploaded.forEach((tag) => {
            this.setProductTagsState([tag.asin!], ProductTagField.FIELD1, tag.field1!);
            this.setProductTagsState([tag.asin!], ProductTagField.FIELD2, tag.field2!);
          });
        },
      },
    });
  }

  private expandProduct(
    product: ProductEx,
    seeds?: AsinSeed[],
    spStrategies?: Strategy[],
    sbStrategies?: Strategy[],
    sdStrategies?: Strategy[],
  ): CatalogProductEx {
    const res: CatalogProductEx = {
      ...product,
      seeds: [],
      blacklist: [],
      keywordSeeds: [],
      productSeeds: [],
      keywordBlacklist: [],
      productBlacklist: [],
      strategies: [
        ...(spStrategies?.filter((s) => s.state == StrategyStateEnum.ENABLED) ?? []),
        ...(sbStrategies?.filter((s) => s.state == StrategyStateEnum.ENABLED) ?? []),
        ...(sdStrategies?.filter((s) => s.state == StrategyStateEnum.ENABLED) ?? []),
      ],
    };

    if (seeds == undefined) return res;

    for (const seed of seeds) {
      if (seed.type == AsinSeedTypeEnum.WHITELIST) {
        res.seeds!.push(seed);
        if (seed.matchType == MatchType.exact || seed.matchType == MatchType.phrase) {
          res.keywordSeeds!.push(seed);
        } else if (seed.matchType == MatchType.asinSameAs) {
          res.productSeeds!.push(seed);
        }
      }
      if (seed.type == AsinSeedTypeEnum.BLACKLIST) {
        res.blacklist!.push(seed);
        if (seed.matchType == MatchType.exact || seed.matchType == MatchType.phrase) {
          res.keywordBlacklist!.push(seed);
        } else if (seed.matchType == MatchType.asinSameAs) {
          res.productBlacklist!.push(seed);
        }
      }
    }
    return res;
  }

  public exportCatalog() {
    if (!this.managedCatalog()) return;
    const params: CsvExportParams = this.getExportParams("catalog");
    const columns: CsvField<CatalogProductEx>[] = [
      simpleField("asin"),
      simpleField("title"),
      simpleField("brand"),
      fieldExtractor("strategies", (product) => (product.strategies?.length ?? 0).toString()),
      this.costOfGoodField,
      fieldExtractor("seeds", (product) => (product.seeds?.length ?? 0).toString()),
      fieldExtractor("blacklist", (product) => (product.blacklist?.length ?? 0).toString()),
      this.productTag1Field,
      this.productTag2Field,
    ];
    this.csvExportService.exportCsv<CatalogProductEx>(params, this.managedCatalog()!, columns);
  }

  public exportSeedsAsCsv(): void {
    const data: SeedCsvRow[] = [];
    for (const product of this.managedCatalog()!) {
      if (product.productSeeds && product.productSeeds?.length > 0) {
        data.push(SeedCsvRow.fromAsinSeed(product.productSeeds)!);
      }
      if (product.keywordSeeds && product.keywordSeeds?.length > 0) {
        const exactKw = product.keywordSeeds.filter((s) => s.matchType == MatchType.exact);
        const phraseKw = product.keywordSeeds.filter((s) => s.matchType == MatchType.phrase);
        if (exactKw.length > 0) {
          data.push(SeedCsvRow.fromAsinSeed(exactKw)!);
        }
        if (phraseKw.length > 0) {
          data.push(SeedCsvRow.fromAsinSeed(phraseKw)!);
        }
      }
      if (product.productBlacklist && product.productBlacklist?.length > 0) {
        data.push(SeedCsvRow.fromAsinSeed(product.productBlacklist)!);
      }
      if (product.keywordBlacklist && product.keywordBlacklist?.length > 0) {
        const exactKw = product.keywordBlacklist.filter((s) => s.matchType == MatchType.exact);
        const phraseKw = product.keywordBlacklist.filter((s) => s.matchType == MatchType.phrase);
        if (exactKw.length > 0) {
          data.push(SeedCsvRow.fromAsinSeed(exactKw)!);
        }
        if (phraseKw.length > 0) {
          data.push(SeedCsvRow.fromAsinSeed(phraseKw)!);
        }
      }
    }
    this.csvExportService.exportCsv(
      {
        prefix: "catalog_seeds",
        accountGroupName: this.accountMarketplace().accountGroupName,
        marketplace: this.accountMarketplace().marketplace,
        quoteStrings: "",
      },
      data,
      [simpleField("asin"), simpleField("type"), simpleField("matchType"), simpleField("value")],
    );
  }

  public exportCogs() {
    if (!this.managedCatalog()) return;

    const params: CsvExportParams = this.getExportParams("cost_of_goods");
    this.csvExportService.exportCsv<CatalogProductEx>(params, this.managedCatalog()!, [
      simpleField("asin"),
      this.costOfGoodField,
    ]);
  }

  public exportProductTags() {
    const params: CsvExportParams = this.getExportParams("product_tags");
    this.csvExportService.exportCsv<CatalogProductEx>(params, this.managedCatalog()!, [
      simpleField("asin"),
      this.productTag1Field,
      this.productTag2Field,
    ]);
  }

  private getExportParams(prefix: string) {
    return {
      prefix: prefix,
      accountGroupName: this.accountMarketplace().accountGroupName,
      marketplace: this.accountMarketplace().marketplace,
    };
  }

  private costOfGoodField: CsvField<CatalogProductEx> = fieldExtractor("costOfGoods", (product) =>
    !product.costOfGoods ? "" : product.costOfGoods.toString(),
  );
  private productTag1Field: CsvField<CatalogProductEx> = fieldExtractor("productTag1", (product) =>
    !product.productTag1 ? "" : product.productTag1,
  );
  private productTag2Field: CsvField<CatalogProductEx> = fieldExtractor("productTag2", (product) =>
    !product.productTag2 ? "" : product.productTag2,
  );
}
